import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton
} from '@mui/material';
import { Search, RefreshCw, AlertCircle } from 'lucide-react';

import { getAllDepartments } from '../data/department';
import { 
  AddAccessibledepartments, 
  LoadRoles, 
  RemoveAccessibledepartments 
} from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';


const Access = () => {
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [departments, setDepartments] = useState(getAllDepartments);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingRoles, setUpdatingRoles] = useState(new Set());

  useEffect(() => {
    loadRoles();
  }, []);

  // Load roles from API
  const loadRoles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(LoadRoles);
      if (response.data.success) {
        setRoles(response.data.results);
      } else {
        throw new Error('Failed to load roles');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load roles';
      setError(errorMessage);
      
      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if user has access to department
  const checkDepartmentAccess = useCallback((user, deptId) => {
    if (!user.department) return false;
    
    // Handle different data formats
    if (Array.isArray(user.department)) {
      return user.department.includes(deptId);
    }
    
    // If it's a comma-separated string
    if (typeof user.department === 'string') {
      const departments = user.department.split(',').map(d => d.trim()).filter(d => d);
      return departments.includes(deptId.toString());
    }
    
    return false;
  }, []);

  // Get accessible department count for a user
  const getAccessibleDepartmentCount = useCallback((user) => {
    if (!user.department) return 0;
    
    if (Array.isArray(user.department)) {
      return user.department.length;
    }
    
    if (typeof user.department === 'string') {
      return user.department.split(',').map(d => d.trim()).filter(d => d).length;
    }
    
    return 0;
  }, []);

  // Update user departments in state
  const updateUserDepartments = useCallback((userRole, deptId, hasAccess) => {
    return roles.map(role => {
      if (role.role === userRole) {
        let updatedDepartments;
        
        if (hasAccess) {
          // Remove department
          if (Array.isArray(role.department)) {
            updatedDepartments = role.department.filter(d => d !== deptId);
          } else if (typeof role.department === 'string') {
            updatedDepartments = role.department
              .split(',')
              .map(d => d.trim())
              .filter(d => d && d !== deptId.toString())
              .join(',');
          } else {
            updatedDepartments = [];
          }
        } else {
          // Add department
          if (Array.isArray(role.department)) {
            updatedDepartments = [...(role.department || []), deptId];
          } else if (typeof role.department === 'string') {
            const existing = role.department ? 
              role.department.split(',').map(d => d.trim()).filter(d => d) : [];
            updatedDepartments = [...existing, deptId.toString()].join(',');
          } else {
            updatedDepartments = [deptId];
          }
        }
        
        return { ...role, department: updatedDepartments };
      }
      return role;
    });
  }, [roles]);

  // Toggle department access with optimistic updates
  const toggleDepartmentAccess = async (userId, deptId, hasAccess, userRole) => {
    // Prevent multiple simultaneous updates for the same role
    if (updatingRoles.has(userRole)) {
      return;
    }

    // Add role to updating set
    setUpdatingRoles(prev => new Set([...prev, userRole]));

    // Store original state for potential rollback
    const originalRoles = [...roles];

    // Optimistic update - update UI immediately
    const updatedRoles = updateUserDepartments(userRole, deptId, hasAccess);
    setRoles(updatedRoles);

    try {
      const endpoint = hasAccess ? RemoveAccessibledepartments : AddAccessibledepartments;
      const response = await axios.post(endpoint, { 
        userRole, 
        deptId: deptId.toString() 
      });
      
      if (response.data.success) {
        // Success - show brief confirmation
        const departmentName = departments.find(d => d.id === deptId)?.name || 'Department';
        
        // Optional: Show success toast (less intrusive than Swal)
        const Toast = Swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
        });
        
        await Toast.fire({
          icon: 'success',
          title: `${departmentName} access ${hasAccess ? 'removed' : 'granted'}`
        });
      } else {
        throw new Error(response.data.message || 'API returned unsuccessful response');
      }
    } catch (error) {
      // Revert optimistic update on error
      setRoles(originalRoles);
      
      const errorMessage = error.response?.data?.message || 'Failed to update access';
      
      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Retry',
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        // Optionally retry the operation
        if (result.isConfirmed) {
          toggleDepartmentAccess(userId, deptId, !hasAccess, userRole);
        }
      });
    } finally {
      // Remove role from updating set
      setUpdatingRoles(prev => {
        const newSet = new Set(prev);
        newSet.delete(userRole);
        return newSet;
      });
    }
  };

 

  // Filter users based on search
  const filteredUsersForAssignment = roles.filter(role =>
    role.role.toLowerCase().includes(assignmentSearch.toLowerCase())
  );

  // Refresh handler
  const handleRefresh = () => {
    loadRoles();
  };

  return (
    <Box sx={{ py: 3 }}>
      <Paper 
        elevation={1} 
        sx={{ 
          p: 3, 
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200'
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="h3" sx={{ fontWeight: 600, color: 'grey.900' }}>
              Department Assignments
            </Typography>
            
            <Tooltip title="Refresh data">
              <IconButton 
                onClick={handleRefresh} 
                disabled={loading}
                size="small"
                sx={{ color: 'grey.500' }}
              >
                <RefreshCw size={18} />
              </IconButton>
            </Tooltip>
            
            {loading && <CircularProgress size={20} />}
          </Box>
          
          <TextField
            placeholder="Search roles for assignment..."
            value={assignmentSearch}
            onChange={(e) => setAssignmentSearch(e.target.value)}
            size="small"
            sx={{ 
              width: { xs: '100%', sm: 320 },
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: 2,
                },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color="#6B7280" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            action={
              <IconButton
                aria-label="retry"
                color="inherit"
                size="small"
                onClick={handleRefresh}
              >
                <RefreshCw size={16} />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && roles.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          /* Role List */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {filteredUsersForAssignment.map((user, index) => {
              const isUpdating = updatingRoles.has(user.role);
              const accessibleCount = getAccessibleDepartmentCount(user);
              
              return (
                <Box key={user.id || user.role}>
                  {/* User Info */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      mb: 2
                    }}
                  >
                    <Box>
                      <Typography variant="h6" component="h4" sx={{ fontWeight: 500, color: 'grey.900' }}>
                        {user.role}
                        {isUpdating && (
                          <CircularProgress 
                            size={16} 
                            sx={{ ml: 1, verticalAlign: 'middle' }} 
                          />
                        )}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'grey.600' }}>
                        Role: {user.role}
                      </Typography>
                    </Box>
                    
                    <Chip 
                      label={`${accessibleCount} department${accessibleCount !== 1 ? 's' : ''} assigned`}
                      size="small"
                      variant="outlined"
                      color={accessibleCount > 0 ? 'primary' : 'default'}
                      sx={{ 
                        color: accessibleCount > 0 ? 'primary.main' : 'grey.600',
                        borderColor: accessibleCount > 0 ? 'primary.main' : 'grey.300'
                      }}
                    />
                  </Box>

                  {/* Department Grid */}
                  <Grid container spacing={1.5} columns={{ xs: 4, sm: 8, md: 12 }}>
                    {departments.map((dept) => {
                      const hasAccess = checkDepartmentAccess(user, dept.id);
                      
                      return (
                        <Grid item xs={12} sm={6} lg={4} key={dept.id} size={4}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              border: '1px solid',
                              borderColor: hasAccess ? 'primary.200' : 'grey.200',
                              backgroundColor: hasAccess ? 'primary.50' : 'grey.50',
                              cursor: isUpdating ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              opacity: isUpdating ? 0.7 : 1,
                              '&:hover': {
                                backgroundColor: !isUpdating ? (hasAccess ? 'primary.100' : 'grey.100') : undefined,
                                transform: !isUpdating ? 'translateY(-1px)' : undefined,
                                boxShadow: !isUpdating ? 1 : undefined,
                              }
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={hasAccess}
                                  onChange={() => toggleDepartmentAccess(user.id, dept.id, hasAccess, user.role)}
                                  size="small"
                                  disabled={isUpdating}
                                  sx={{
                                    color: 'primary.main',
                                    '&.Mui-checked': {
                                      color: 'primary.main',
                                    },
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 500, 
                                      color: 'grey.900', 
                                      lineHeight: 1.2 
                                    }}
                                  >
                                    {dept.name}
                                  </Typography>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: 'grey.600', 
                                      display: 'block', 
                                      mt: 0.25 
                                    }}
                                  >
                                    {dept.description}
                                  </Typography>
                                </Box>
                              }
                              sx={{ 
                                margin: 0,
                                alignItems: 'flex-start',
                                width: '100%',
                                '& .MuiFormControlLabel-label': {
                                  paddingLeft: 1,
                                  width: '100%'
                                }
                              }}
                            />
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {/* Divider (except for last item) */}
                  {index < filteredUsersForAssignment.length - 1 && (
                    <Divider sx={{ mt: 3 }} />
                  )}
                </Box>
              );
            })}

            {/* No Results Message */}
            {filteredUsersForAssignment.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AlertCircle size={48} color="#9CA3AF" />
                <Typography variant="h6" sx={{ color: 'grey.500', mt: 2 }}>
                  No roles found
                </Typography>
                <Typography variant="body2" sx={{ color: 'grey.400' }}>
                  {assignmentSearch ? 
                    'Try adjusting your search terms' : 
                    'No roles available to assign departments to'
                  }
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Access